import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, GitCompare } from "lucide-react";
import { api, formatCOP, formatDate } from "../../lib/apiClient";

const MAX_DIFF_WORDS = 500;

function diffWords(a, b) {
  const x = (a || "").split(/(\s+)/), y = (b || "").split(/(\s+)/);
  if (x.length > MAX_DIFF_WORDS * 2 || y.length > MAX_DIFF_WORDS * 2) return null;
  const n = x.length, m = y.length;
  const L = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) L[i][j] = x[i] === y[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const before = [], after = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (x[i] === y[j]) { before.push([x[i], false]); after.push([y[j], false]); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) before.push([x[i++], true]);
    else after.push([y[j++], true]);
  }
  while (i < n) before.push([x[i++], true]);
  while (j < m) after.push([y[j++], true]);
  return { before, after };
}

function Marked({ tokens, cls }) {
  return tokens.map(([t, mark], k) => (mark && t.trim() ? <mark key={k} className={cls}>{t}</mark> : <React.Fragment key={k}>{t}</React.Fragment>));
}

function Side({ title, text, tokens, markCls, tone, testid }) {
  return (
    <div className={`border p-3 ${tone}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">{title}</p>
      <p className="text-xs text-gray-800 whitespace-pre-line">
        {!text ? <em className="text-gray-400">Sin contenido</em> : tokens ? <Marked tokens={tokens} cls={markCls} /> : text}
      </p>
    </div>
  );
}

function SectionDiff({ s, from, to }) {
  const [open, setOpen] = useState(false);
  const d = open ? diffWords(s.before, s.after) : null;
  return (
    <div className="border-t border-gray-100 pt-2" data-testid={`diagnosis-compare-section-${s.key}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left text-sm font-medium text-gray-900" data-testid={`diagnosis-compare-section-toggle-${s.key}`}>
        {s.label} {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="grid md:grid-cols-2 gap-2 mt-1.5">
          <Side title={`Antes (v${from.version})`} text={s.before} tokens={d?.before} markCls="bg-rose-100 text-rose-900 line-through decoration-rose-400" tone="border-gray-200 bg-gray-50" testid={`diagnosis-compare-before-${s.key}`} />
          <Side title={`Ahora (v${to.version})`} text={s.after} tokens={d?.after} markCls="bg-emerald-100 text-emerald-900" tone="border-emerald-200 bg-emerald-50/40" testid={`diagnosis-compare-after-${s.key}`} />
        </div>
      )}
    </div>
  );
}

export function DiagnosisCompare({ caseId, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/cases/${caseId}/diagnosis/compare`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [caseId, refreshKey]);
  if (!data?.available) return null;
  const { from, to } = data;
  return (
    <section className="border border-gray-200 bg-white p-4" data-testid="diagnosis-compare">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="diagnosis-compare-toggle">
        <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <GitCompare className="w-4 h-4 text-primary" />
          Qué cambió: versión {from.version} → versión {to.version} ({data.changed.length} sección(es))
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          <p className="text-xs text-gray-600" data-testid="diagnosis-compare-meta">
            v{from.version} ({formatDate(from.created_at)}) con presupuesto {from.budget_cop ? formatCOP(from.budget_cop) : "—"} ·
            v{to.version} ({formatDate(to.created_at)}) con presupuesto {to.budget_cop ? formatCOP(to.budget_cop) : "—"}
          </p>
          {data.changed.length === 0 && <p className="text-xs text-gray-600">No hay diferencias de contenido entre las dos versiones.</p>}
          {data.changed.length > 0 && <p className="text-[11px] text-gray-500">Abra cada sección para ver el texto anterior y el nuevo; lo que cambió aparece resaltado.</p>}
          {data.changed.map((s) => <SectionDiff key={s.key} s={s} from={from} to={to} />)}
          {data.unchanged.length > 0 && (
            <p className="text-[11px] text-gray-500" data-testid="diagnosis-compare-unchanged">Sin cambios: {data.unchanged.join(", ")}.</p>
          )}
        </div>
      )}
    </section>
  );
}
